'use client';

import { useMemo, useState, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Text, useResizeObserver, useTranslation, useUi } from '@weareconceptstudio/core';

//* Libs
import { fetchData } from '@/libs/get-data';

//* Styles
import FAQCompStyle from './style';

//* HOCs
import { withDataContext } from '@/context';

//* Components
import Tab from '@/components/Tab';
import Editor from '@/components/Editor';
import FaqItem from './Item';
import TitleAnim from '@/modules/_animation/Title';

gsap.registerPlugin(ScrollTrigger);

const FAQComp = ({ globalData }) => {
	const { selectedLang } = useTranslation();
	const { winWidth } = useUi();
	const { title, description } = globalData.faq;

	//! Refs
	const contentItemRefs = useRef([]);
	const tabRefWrapper = useRef();
	const descriptionRef = useRef();
	const triggerRef = useRef();
	const styleRef = useRef();

	//! State
	const [activeTab, setActiveTab] = useState(null);
	const [data, setData] = useState([]);

	const [dimensions, setDimensions] = useState({
		headerHeight: 0,
		distance: 0,
		stickyWrapHeight: 0,
	});

	const { headerHeight, distance, stickyWrapHeight } = dimensions;
	const resizes = useResizeObserver(typeof window !== 'undefined' ? document.body : null);

	const isMobile = useMemo(() => winWidth < 1024, [winWidth]);

	//! Load FAQs
	useEffect(() => {
		let mounted = true;
		fetchData({ name: 'faqs', lang: selectedLang }).then((result) => {
			if (mounted) setData(result);
		});
		return () => {
			mounted = false;
		};
	}, [selectedLang]);

	//! Calculate element sizes
	useEffect(() => {
		if (typeof window === 'undefined') return;

		const getElementSize = (selector, prop = 'clientHeight') => document.querySelector(selector)?.[prop] ?? 0;

		const wrapperEl = document.querySelector('.faq-template-wrapper');
		const distance = wrapperEl ? parseInt(getComputedStyle(wrapperEl).marginBlockStart || 0) : 0;

		setDimensions({
			headerHeight: getElementSize('header'),
			distance,
			stickyWrapHeight: getElementSize('.faq-sticky-wrapper'),
		});
	}, [resizes, winWidth]);

	//! IntersectionObserver for tab activation
	useEffect(() => {
		if (!contentItemRefs.current.length) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const index = contentItemRefs.current.findIndex((ref) => ref === entry.target);
						if (index !== -1) {
							setActiveTab(data[index]?.name);
						}
					}
				});
			},
			{
				root: null,
				rootMargin: `-${headerHeight + distance}px 0px -50% 0px`,
				threshold: 0.1,
			}
		);

		const refsSnapshot = [...contentItemRefs.current];
		refsSnapshot.forEach((ref) => ref && observer.observe(ref));

		return () => {
			refsSnapshot.forEach((ref) => ref && observer.unobserve(ref));
		};
	}, [data, headerHeight, distance]);

	//! GSAP Sticky Logic
	useEffect(() => {
		const matchMedia = gsap.matchMedia();

		if (resizes && typeof window !== 'undefined') {
			matchMedia.add('(min-width: 1024px)', () => {
				const sticky = document.querySelector('.faq-sticky-wrapper');
				const leftBar = document.querySelector('.faq-left-bar');
				const tapSticky = document.querySelector('.tap-sticky-wrapper');

				if (!sticky || !leftBar || !tapSticky) return;

				gsap.to(sticky, {
					scrollTrigger: {
						trigger: leftBar,
						start: `top ${headerHeight + distance}px`,
						end: `bottom ${headerHeight + distance + stickyWrapHeight}px`,
						pin: sticky,
						pinSpacing: false,
					},
				});

				gsap.to(tapSticky, {
					scrollTrigger: {
						trigger: leftBar,
						start: `top ${headerHeight + distance}px`,
						end: `bottom ${headerHeight + distance + stickyWrapHeight}px`,
						pin: tapSticky,
						pinSpacing: false,
					},
				});
			});
		}

		return () => matchMedia.revert();
	}, [resizes, headerHeight, distance, stickyWrapHeight, winWidth]);

	//! Scroll to tab content
	const scrollToContent = (tabName) => {
		const contentIndex = data.findIndex((item) => item.name === tabName);
		if (contentIndex === -1 || !contentItemRefs.current[contentIndex]) return;

		const contentElement = contentItemRefs.current[contentIndex];
		const offset = headerHeight + distance;

		setActiveTab(tabName);

		gsap.to(window, {
			duration: 0.7,
			scrollTo: {
				y: contentElement,
				offsetY: offset,
				autoKill: false,
			},
			ease: 'power2.inOut',
		});
	};

	//! Generate tab content
	const tabContent = useMemo(
		() =>
			data.map((item, index) => (
				<FaqItem
					key={item.name}
					isFirst={index === 0}
					data={item}
					animRef={styleRef}
					ref={(ref) => (contentItemRefs.current[index] = ref)}
				/>
			)),
		[data]
	);

	//! Animate tabs & description on enter
	useEffect(() => {
		const triggerEl = styleRef.current;
		const descEl = descriptionRef.current;
		const tabChildren = tabRefWrapper.current?.children;

		triggerRef.current = ScrollTrigger.create({
			start: 'top bottom',
			end: 'top top',
			trigger: triggerEl,
			onEnter: () => {
				if (descEl) {
					gsap.fromTo(descEl, { opacity: 0 }, { opacity: 1, duration: 0.5, delay: 0.4 });
				}
				if (tabChildren?.length) {
					gsap.fromTo(tabChildren, { scale: 0 }, { scale: 1, duration: 0.6, delay: 0.5, stagger: 0.1 });
				}
			},
			onLeaveBack: () => {
				if (descEl) gsap.set(descEl, { opacity: 0 });
				if (tabChildren?.length) gsap.set(tabChildren, { scale: 0 });
			},
		});

		return () => {
			triggerRef.current?.kill();
			if (descEl) gsap.killTweensOf(descEl);
			if (tabChildren?.length) gsap.killTweensOf(tabChildren);
		};
	}, [isMobile, data]);

	if (!data.length) return null;

	return (
		<FAQCompStyle
			ref={styleRef}
			className='faq-template-wrapper'
		>
			<Container
				row
				isSection
				className='faq-version'
			>
				<div className='col-4 col-dx-12 faq-left-bar'>
					<div className='faq-sticky-wrapper'>
						<TitleAnim
							load
							trigger='bottom'
						>
							<Text
								tag='h1'
								className='h2 font-larken-light green-1000 title-faq'
							>
								{title}
							</Text>
						</TitleAnim>
						<div
							ref={descriptionRef}
							className='description-wrapper opacity-zero'
						>
							<Editor
								data={description}
								className='desc-faq'
							/>
						</div>
					</div>
				</div>

				<div className='empty-col col-1 col-dx-12' />

				<div className='col-7 col-dx-12 faq-items'>
					<div className='faq-inner-items'>
						<div className={`tap-sticky-wrapper`}>
							{!isMobile ? (
								<div
									ref={tabRefWrapper}
									className='faq-tab-wrapper'
								>
									{data.map((item) => (
										<Tab
											key={item.name}
											text={item.name}
											active={item.name === activeTab}
											onClick={() => scrollToContent(item.name)}
										/>
									))}
								</div>
							) : null}
						</div>

						<div className='tab-container'>{tabContent}</div>
					</div>
				</div>
			</Container>
		</FAQCompStyle>
	);
};

export default withDataContext(FAQComp, ['globalData']);
